/**
 * Edit Financial Information Modal
 *
 * Modal for editing vendor financial information including:
 * - Payment Terms (deposit, cash discount, terms type, days)
 * - Tax Information (tax type, rate, ABN)
 * - Primary Currency
 *
 * Uses React Hook Form + gray section styling matching create vendor wizard
 */

import { useMemo, useEffect } from 'react';
import { Modal, Input, Select, Row, Col, Typography, theme } from 'antd';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { usePaymentTerms } from '@/hooks/usePaymentTerms';
import { useTaxTypes } from '@/hooks/useTaxTypes';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { editFinancialInfoSchema } from '@/schemas/forms';
import type { VendorPaymentDataResponse } from '@/types/api/paymentTerm';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';

const { Text } = Typography;

interface EditFinancialInfoFormData {
  deposit?: number;
  cashDiscount?: number;
  cashDiscountRate?: number;
  cashDiscountDays?: number;
  termsTypeId: number;
  termsDays: number;
  primaryCurrency: string;
  abn: string;
  taxTypeId: number;
  taxRate: number;
}

export interface EditFinancialInfoModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditFinancialInfoFormData) => Promise<void>;
  payment: VendorPaymentDataResponse | null;
  countryId?: number;
  localTaxId?: string | null;
  loading?: boolean;
}

export const EditFinancialInfoModal: React.FC<EditFinancialInfoModalProps> = ({
  open,
  onCancel,
  onSave,
  payment,
  countryId,
  localTaxId,
  loading = false,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { handleError, handleSuccess } = useErrorHandler();

  // Fetch payment terms, tax types, and currencies (typeId: 1 = Vendor)
  const { paymentTerms, isLoading: isLoadingPaymentTerms } = usePaymentTerms(1);
  const { taxTypes, isLoading: isLoadingTaxTypes } = useTaxTypes(countryId);
  const { currencyOptions, isLoading: isLoadingCurrencies } = useCurrencies();

  // Compute form values from payment data using useMemo (prevents unnecessary recalculations)
  const formValues = useMemo(() => {
    if (!payment) {
      return undefined;
    }
    return {
      deposit: payment.depositPercentage ?? undefined,
      cashDiscount: payment.cashDiscountPercentage ?? undefined,
      cashDiscountRate: payment.paymentTerm?.customDiscountRate ?? undefined,
      cashDiscountDays: payment.paymentTerm?.customDiscountDays ?? undefined,
      termsTypeId: payment.paymentTerm?.basePaymentTermId || 1,
      termsDays: payment.paymentTerm?.customDueDays || 0,
      primaryCurrency: payment.primaryCurrency || 'USD',
      abn: localTaxId || '', // ABN from vendor detail
      taxTypeId: payment.taxTypeId || 1,
      taxRate: payment.taxRate || 0,
    };
  }, [payment, localTaxId]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditFinancialInfoFormData>({
    resolver: zodResolver(editFinancialInfoSchema),
    defaultValues: {
      deposit: undefined,
      cashDiscount: undefined,
      cashDiscountRate: undefined,
      cashDiscountDays: undefined,
      termsTypeId: 1,
      termsDays: 0,
      primaryCurrency: 'USD',
      abn: '',
      taxTypeId: 1,
      taxRate: 0,
    },
    // Use `values` prop for reactive updates from external data (recommended by RHF docs)
    values: formValues,
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  // Use useWatch for better performance (doesn't cause full form re-render)
  const selectedTermsTypeId = useWatch({ control, name: 'termsTypeId' });
  const selectedTaxTypeId = useWatch({ control, name: 'taxTypeId' });

  // Auto-update tax rate when tax type changes (wrapped in useEffect to avoid setState during render)
  useEffect(() => {
    const selectedTaxType = taxTypes.find((t) => t.taxTypeId === selectedTaxTypeId);
    if (selectedTaxType) {
      setValue('taxRate', selectedTaxType.taxRate);
    }
  }, [selectedTaxTypeId, taxTypes, setValue]);

  // Auto-update term days when payment term changes (wrapped in useEffect to avoid setState during render)
  useEffect(() => {
    const selectedTerm = paymentTerms.find((term) => term.paymentTermId === selectedTermsTypeId);
    if (selectedTerm) {
      setValue('termsDays', selectedTerm.dueDays);
    }
  }, [selectedTermsTypeId, paymentTerms, setValue]);

  const handleFormSubmit = async (data: EditFinancialInfoFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('vendor.messages.financialInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Override formStyles with generous spacing (matching EditBasicInfoModal)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized styles from formStyles (matching EditBasicInfoModal pattern)
  const sectionTitleStyle = formStyles.sectionTitleStyle;
  const labelStyle = formStyles.labelStyle;
  const inputStyle = formStyles.inputStyle;

  // Payment terms options
  const paymentTermOptions = useMemo(() => {
    return paymentTerms.map((term) => ({
      label: `${term.termNameEn} (${term.termCode})`,
      value: term.paymentTermId,
    }));
  }, [paymentTerms]);

  // Tax type options
  const taxTypeOptions = useMemo(() => {
    return taxTypes.map((taxType) => ({
      label: `${taxType.taxType} (${taxType.taxRate}%)`,
      value: taxType.taxTypeId,
    }));
  }, [taxTypes]);

  // Currency options are now fetched from the API via useCurrencies hook

  // Check selected payment term properties
  const selectedPaymentTerm = paymentTerms.find((term) => term.paymentTermId === selectedTermsTypeId);
  const isZeroDayTerm = selectedPaymentTerm?.dueDays === 0;
  const termHasDiscount = selectedPaymentTerm?.hasDiscount === true;

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={t('vendor.modals.editFinancialInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('vendor.actions.save')}
        cancelText={t('vendor.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={700}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
        {/* Payment Terms Section */}
        <div style={fieldContainerStyle}>
          <Text strong style={sectionTitleStyle}>
            {t('vendor.sections.paymentTerms')}
          </Text>

          {/* Row 1: Deposit + Cash Discount (always visible) */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.labels.deposit')}</Text>
              <Controller
                name="deposit"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t('vendor.placeholders.depositPlaceholder')}
                    suffix="%"
                    allowClear
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      field.onChange(value);
                    }}
                    style={inputStyle}
                  />
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.labels.cashDiscount')}</Text>
              <Controller
                name="cashDiscount"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t('vendor.placeholders.cashDiscountPlaceholder')}
                    suffix="%"
                    allowClear
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      field.onChange(value);
                    }}
                    style={inputStyle}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Row 2: Agreed Terms + Due In Days */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.agreedTerms')}
              </Text>
              <Controller
                name="termsTypeId"
                control={control}
                rules={{ required: t('vendor.validation.paymentTermsRequired') }}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.selectPaymentTerms')}
                      loading={isLoadingPaymentTerms}
                      options={paymentTermOptions}
                      style={{
                        ...inputStyle,
                        borderColor: errors.termsTypeId ? token.colorError : undefined,
                      }}
                    />
                    {errors.termsTypeId && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.termsTypeId.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.days')}</Text>
              <Controller
                name="termsDays"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    type="number"
                    placeholder={isZeroDayTerm ? t('vendor.paymentTerms.notApplicable') : t('vendor.placeholders.enterDays')}
                    suffix={t('vendor.misc.suffix.days')}
                    disabled={isZeroDayTerm}
                    value={isZeroDayTerm ? '' : (field.value ?? '')}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      field.onChange(value);
                    }}
                    style={{
                      ...inputStyle,
                      backgroundColor: isZeroDayTerm
                        ? token.colorBgContainerDisabled
                        : token.colorBgContainer,
                    }}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Row 3: Cash Discount Rate + Discount Days (only when payment term has discount) */}
          {termHasDiscount && (
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={labelStyle}>{t('vendor.fields.cashDiscountRate')}</Text>
                <Controller
                  name="cashDiscountRate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={selectedPaymentTerm?.discountRate?.toString() || '0'}
                      suffix="%"
                      allowClear
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('vendor.fields.cashDiscountDays')}</Text>
                <Controller
                  name="cashDiscountDays"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      min={0}
                      placeholder={selectedPaymentTerm?.discountDays?.toString() || '0'}
                      suffix={t('vendor.misc.suffix.days')}
                      allowClear
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
            </Row>
          )}

          {/* Row 4: Primary Currency */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.primaryCurrency')}
              </Text>
              <Controller
                name="primaryCurrency"
                control={control}
                rules={{ required: t('vendor.validation.primaryCurrencyRequired') }}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.currency')}
                      showSearch
                      loading={isLoadingCurrencies}
                      options={currencyOptions}
                      style={{
                        ...inputStyle,
                        borderColor: errors.primaryCurrency ? token.colorError : undefined,
                      }}
                    />
                    {errors.primaryCurrency && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.primaryCurrency.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </div>

        {/* Tax Information Section */}
        <div style={fieldContainerStyle}>
          <Text strong style={sectionTitleStyle}>
            {t('vendor.sections.taxInfo')}
          </Text>

          {/* Row 1: ABN (Display only - from step 1) */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text style={labelStyle}>{t('vendor.fields.taxIdAbn')}</Text>
              <Controller
                name="abn"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly
                    style={{
                      ...inputStyle,
                      backgroundColor: token.colorBgContainer,
                      color: token.colorText,
                      cursor: 'not-allowed',
                    }}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Row 2: Tax Type + Tax Rate */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.taxType')}
              </Text>
              <Controller
                name="taxTypeId"
                control={control}
                rules={{ required: t('vendor.validation.taxTypeRequired') }}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.taxType')}
                      loading={isLoadingTaxTypes}
                      options={taxTypeOptions}
                      style={{
                        ...inputStyle,
                        borderColor: errors.taxTypeId ? token.colorError : undefined,
                      }}
                    />
                    {errors.taxTypeId && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.taxTypeId.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.taxRatePercent')}</Text>
              <Controller
                name="taxRate"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    type="number"
                    placeholder={t('vendor.placeholders.autoCalculated')}
                    suffix="%"
                    readOnly
                    style={{
                      ...inputStyle,
                      backgroundColor: token.colorBgContainer,
                      color: token.colorText,
                      cursor: 'not-allowed',
                    }}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
      </form>
    </Modal>
    </>
  );
};

export default EditFinancialInfoModal;
